import { DateFormatter, ZonedDateTime } from "@internationalized/date"
import { NumberFormatter } from "@internationalized/number"
import { StripePaymentMethod } from "ipctg-inflow-types/InFlowAPIMisc"
import { CardBrand } from "ipctg-inflow-types/InFlowEnums"

import { IconName } from "@/assets/icons"

import { US_LOCALE } from "./localization"

export const UNKNOWN_PLATE_PREFIX = "UNKNOWN"

export const cn = (...objs: any) => objs.flat().filter(Boolean).join(" ")

export const stripEmptyParams = <T extends Record<string, unknown>>(
  search: T
) => {
  return Object.entries(search).reduce(
    (acc, [key, value]) => {
      if (
        value === undefined ||
        value === "" ||
        (typeof value === "number" && isNaN(value))
      ) {
        delete acc[key]
      }
      return acc
    },
    { ...search }
  )
}

export function cleanEmptyParams<T extends Record<string, unknown>>(search: T) {
  return Object.entries(search).reduce(
    (acc, [key, value]) => {
      if (
        value === undefined ||
        value === "" ||
        (typeof value === "number" && isNaN(value))
      ) {
        delete acc[key]
      }
      return acc
    },
    { ...search }
  )
}

export function getLocale() {
  return typeof window !== "undefined" ? navigator.language : US_LOCALE
}

export function formatZonedDateTimeWithTimeZone(zdt: ZonedDateTime) {
  const dateFormatter = new DateFormatter(getLocale(), {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: zdt.timeZone,
  })
  const timeFormatter = new DateFormatter(getLocale(), {
    hour: "numeric",
    minute: "numeric",
    timeZone: zdt.timeZone,
    timeZoneName: "short",
  })

  return `${dateFormatter.format(zdt.toDate())}, ${timeFormatter.format(zdt.toDate())}`
}

export function percentFormatter(amount: number) {
  const percentFormatter = new NumberFormatter(getLocale(), {
    style: "percent",
    maximumFractionDigits: 3,
  })

  return percentFormatter.format(amount)
}

export function upperFirst(str: string): string {
  if (!str) {
    return str
  }
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function startCase(input: string): string {
  const replaced = input.replace(/[_-]+/g, " ")
  const withSpaces = replaced.replace(/([a-z])([A-Z])/g, "$1 $2")

  return withSpaces
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
}

export function camelCase(input: string): string {
  const replaced = input.replace(/[_-]+/g, " ")
  const withSpaces = replaced.replace(/([a-z])([A-Z])/g, "$1 $2")
  const words = withSpaces
    .trim()
    .split(/\s+/)
    .map((word) => word.toLowerCase())

  if (words.length === 0) return ""

  return (
    words[0] +
    words
      .slice(1)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("")
  )
}

export function apiCardBrandToIconName(
  paymentMethod: StripePaymentMethod
): IconName {
  switch (paymentMethod.cardBrand) {
    case CardBrand.Visa:
      return "paymentVisa"
    case CardBrand.Mastercard:
      return "paymentMastercard"
    case CardBrand.AmericanExpress:
      return "paymentAmericanExpress"
    case CardBrand.Discover:
      return "paymentDiscover"
    default:
      return "creditCard"
  }
}
